import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  CircularProgress,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  IconButton,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  Divider,
  Tooltip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { 
  Business as BusinessIcon,
  Close as CloseIcon,
  ShoppingBasket as PurchaseOrderIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import Layout from '../layout/Layout';
import purchaseOrderService from '../../services/purchaseOrderService';
import ProductSearchBar from './ProductSearchBar';
import POPayment from './POPayment';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

/**
 * PurchaseOrder main component
 * @returns {JSX.Element} Purchase Order screen
 */
const PurchaseOrder = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [processingOrder, setProcessingOrder] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [supplierSearchQuery, setSupplierSearchQuery] = useState('');
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Load suppliers on component mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        setLoadingSuppliers(true);
        const data = await purchaseOrderService.getAllSuppliers();
        // Ensure data is always an array
        const suppliersArray = Array.isArray(data) ? data : (data?.content || data?.data || []);
        setSuppliers(suppliersArray);
      } catch (error) {
        console.error('Error loading suppliers:', error);
        setSuppliers([]); // Set empty array on error
        enqueueSnackbar('Failed to load suppliers', { variant: 'error' });
      } finally {
        setLoadingSuppliers(false);
      }
    };

    fetchSuppliers();
  }, [enqueueSnackbar]);

  // Filter suppliers based on search query
  useEffect(() => {
    if (!supplierSearchQuery.trim()) {
      setFilteredSuppliers(Array.isArray(suppliers) ? suppliers : []);
      return;
    }

    const query = supplierSearchQuery.toLowerCase();
    const suppliersArray = Array.isArray(suppliers) ? suppliers : [];
    const filtered = suppliersArray.filter(
      supplier =>
        (supplier.name && supplier.name.toLowerCase().includes(query)) ||
        (supplier.companyName && supplier.companyName.toLowerCase().includes(query)) ||
        (supplier.company_name && supplier.company_name.toLowerCase().includes(query)) ||
        (supplier.phone && supplier.phone.toLowerCase().includes(query)) ||
        (supplier.mobileNo && supplier.mobileNo.toLowerCase().includes(query))
    );

    setFilteredSuppliers(filtered);
  }, [supplierSearchQuery, suppliers]);

  // Update filtered suppliers when suppliers list changes
  useEffect(() => {
    setFilteredSuppliers(Array.isArray(suppliers) ? suppliers : []);
  }, [suppliers]);

  // Handle product selection from search
  const handleAddProduct = (product) => {
    const existingItem = items.find(item => item.product.id === product.id);
    
    // Use cost price for purchase orders, with fallback options
    const price = product.costPrice || product.purchasePrice || 0;
    
    if (existingItem) {
      const updatedItems = items.map(item =>
        item.product.id === product.id
          ? { 
              ...item, 
              quantity: item.quantity + 1,
              total: (item.quantity + 1) * item.unitPrice
            }
          : item
      );
      setItems(updatedItems);
    } else {
      setItems([...items, { 
        product, 
        quantity: 1, 
        unitPrice: price,
        tax: product.tax || null,
        total: price
      }]);
    }
    
    setShowSearchResults(false);
  };

  // Remove product from items
  const handleRemoveItem = (productId) => {
    const updatedItems = items.filter(item => item.product.id !== productId);
    setItems(updatedItems);
  };

  // Update item quantity
  const handleUpdateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      return;
    }
    
    const updatedItems = items.map(item => {
      if (item.product.id === productId) {
        return { 
          ...item, 
          quantity, 
          total: quantity * item.unitPrice
        };
      }
      return item;
    });
    
    setItems(updatedItems);
  };

  // Update item unit price
  const handleUpdateUnitPrice = (productId, unitPrice) => {
    if (unitPrice < 0) {
      return;
    }
    
    const updatedItems = items.map(item => {
      if (item.product.id === productId) {
        return { 
          ...item, 
          unitPrice,
          total: item.quantity * unitPrice
        };
      }
      return item;
    });
    
    setItems(updatedItems);
  };

  // Update item discount percentage
  const handleUpdateDiscountPercent = (productId, discountPercent) => {
    if (discountPercent < 0 || discountPercent > 100) {
      return;
    }
    
    const updatedItems = items.map(item => {
      if (item.product.id === productId) {
        const subtotal = item.unitPrice * item.quantity;
        const discountAmount = discountPercent > 0 ? subtotal * (discountPercent / 100) : 0;
        const afterDiscount = subtotal - discountAmount;
        const taxAmount = item.tax ? afterDiscount * (item.tax.rate / 100) : 0;
        
        return { 
          ...item, 
          discountPercent, 
          discount: discountAmount,
          productTax: taxAmount,
          total: afterDiscount,
          netCost: afterDiscount + taxAmount
        };
      }
      return item;
    });
    
    setItems(updatedItems);
  };

  // Calculate order totals
  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    
    // Calculate tax amount
    const taxAmount = items.reduce((sum, item) => {
      const itemTotal = item.quantity * item.unitPrice;
      const taxRate = item.product.tax?.rate || 0;
      return sum + (itemTotal * (taxRate / 100));
    }, 0);
    
    return {
      subtotal,
      taxAmount,
      total: subtotal + taxAmount,
      totalItems: items.length,
      totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0)
    };
  };

  // Handle opening the payment dialog
  const handleOpenPayment = () => {
    if (items.length === 0) {
      enqueueSnackbar('Please add at least one product to the purchase order', { variant: 'warning' });
      return;
    }
    
    if (!supplier) {
      enqueueSnackbar('Please select a supplier', { variant: 'warning' });
      return;
    }
    
    setPaymentDialogOpen(true);
  };

  // Handle completing the purchase order
  const handleCompletePurchase = async (paymentData) => {
    try {
      setProcessingOrder(true);
      setErrorMessage(null);
      
      // Get the purchase date from state, or use today if not set
      const formattedDate = purchaseDate || new Date().toISOString().split('T')[0];
      
      // Prepare purchase order data
      const purchaseOrder = {
        supplierId: supplier?.id,
        orderDate: formattedDate,
        companyId: JSON.parse(localStorage.getItem('user'))?.companyId || 1,
        invoiceNumber: invoiceNumber || null,
        invoiceDate: invoiceDate || null,
        items: items.map(item => ({
          productId: item.product.id,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discount: 0,
          taxId: item.product.tax?.id || null
        })),
        paymentMethod: paymentData.method,
        status: "RECEIVED_COMPLETE", // Default status
        notes: paymentData.notes || '',
        estimatedDeliveryDate: paymentData.estimatedDelivery || null,
        subtotal: calculateTotals().subtotal,
        taxAmount: calculateTotals().taxAmount,
        total: calculateTotals().total
      };
      
      console.log('Sending purchase order data:', JSON.stringify(purchaseOrder, null, 2));
      
      // Call API to create purchase order
      const result = await purchaseOrderService.createPurchaseOrder(purchaseOrder);
      
      // Store the current order
      setCurrentOrder(result);
      
      // Show success message
      setSuccessMessage(`Purchase Order #${result.id || 'New'} created successfully!`);
      enqueueSnackbar(`Purchase order created successfully!`, { 
        variant: 'success',
      });
      
      // Reset state to create a new order
      setTimeout(() => {
        setItems([]);
        setPaymentDialogOpen(false);
        setProcessingOrder(false);
        
        // After a brief delay to show the success message, refresh the page 
        setTimeout(() => {
          // Option 1: Reload the current page to refresh all data
          window.location.reload();
        }, 1000);
      }, 2000);
      
    } catch (error) {
      console.error('Error creating purchase order:', error);
      setErrorMessage(`Failed to create purchase order: ${error.message || 'Unknown error'}`);
      enqueueSnackbar(`Error: ${error.message || 'Failed to create purchase order'}`, { 
        variant: 'error',
      });
      setProcessingOrder(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    // Trigger search after a short delay
    const delayDebounceFn = setTimeout(() => {
      searchProducts(query);
    }, 300);
    
    return () => clearTimeout(delayDebounceFn);
  };

  // Search for products
  const searchProducts = async (query) => {
    if (!query || query.length < 2) return;
    
    try {
      setSearchLoading(true);
      setShowSearchResults(true);
      const results = await purchaseOrderService.searchProducts(query);
      // Filter out inactive products
      const activeProducts = results.filter(product => 
        product.status === undefined || product.status === null || product.status.toUpperCase() === 'ACTIVE'
      );
      setSearchResults(Array.isArray(activeProducts) ? activeProducts : activeProducts.content || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PurchaseOrderIcon sx={{ mr: 1 }} />
                Purchase Orders
              </Typography>
            
            {/* Message area for errors/success */}
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
                {errorMessage}
              </Alert>
            )}
            
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
                {successMessage}
              </Alert>
            )}
            
        <StyledPaper>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label="Purchase Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={purchaseDate || new Date().toISOString().split('T')[0]}
                onChange={(e) => setPurchaseDate(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Invoice Number"
                fullWidth
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                size="small"
                placeholder="Enter invoice #"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Invoice Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={invoiceDate || ''}
                onChange={(e) => setInvoiceDate(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Supplier"
                fullWidth
                value={supplierSearchQuery}
                onChange={(e) => {
                  setSupplierSearchQuery(e.target.value);
                  // Auto-open dropdown when typing
                  setShowSupplierDropdown(true);
                }}
                onClick={() => setShowSupplierDropdown(true)}
                onFocus={() => setShowSupplierDropdown(true)}
                onBlur={() => {
                  // Small delay to allow clicking dropdown items
                  setTimeout(() => {
                    if (!supplier && supplierSearchQuery) {
                      const matchingSupplier = filteredSuppliers.find(s => 
                        (s.name && s.name.toLowerCase() === supplierSearchQuery.toLowerCase()) ||
                        (s.companyName && s.companyName.toLowerCase() === supplierSearchQuery.toLowerCase()) ||
                        (s.company_name && s.company_name.toLowerCase() === supplierSearchQuery.toLowerCase())
                      );
                      if (matchingSupplier) setSupplier(matchingSupplier);
                    }
                    setShowSupplierDropdown(false);
                  }, 250);
                }}
                size="small"
                placeholder="Search and select supplier"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon />
                    </InputAdornment>
                  ),
                  endAdornment: loadingSuppliers ? (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ) : supplier ? (
                    <InputAdornment position="end">
                      <Tooltip title="Clear selection">
                        <IconButton 
                          edge="end" 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSupplier(null);
                            setSupplierSearchQuery('');
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null
                }}
              />
              {showSupplierDropdown && (
                <Paper
                  elevation={3}
                  sx={{
                    position: 'absolute',
                    zIndex: 1300,
                    width: 'calc(100% - 16px)',
                    maxHeight: 350,
                    overflow: 'auto',
                    mt: 0.5
                  }}
                >
                  {(Array.isArray(filteredSuppliers) ? filteredSuppliers : []).length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography color="textSecondary">
                        No suppliers found
                      </Typography>
                    </Box>
                  ) : (
                    <List dense component="nav">
                      {(Array.isArray(filteredSuppliers) ? filteredSuppliers : []).map((sup) => (
                        <ListItem
                          button
                          key={sup.id}
                          selected={supplier?.id === sup.id}
                          onClick={() => {
                            setSupplier(sup);
                            const displayName = sup.name || sup.companyName || sup.company_name;
                            setSupplierSearchQuery(displayName);
                            setShowSupplierDropdown(false);
                          }}
                        >
                          <ListItemText
                            primary={sup.name || sup.companyName || sup.company_name}
                            secondary={sup.phone || sup.mobileNo || 'No phone'}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              )}
            </Grid>
          </Grid>
        </StyledPaper>
        
        <StyledPaper>
          <Box sx={{ mb: 3, position: 'relative' }}>
            <Box sx={{ display: 'flex' }}>
              <TextField
                fullWidth
                placeholder="Enter Product name / SKU / Scan bar code"
                variant="outlined"
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchLoading && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => searchProducts(searchQuery)}
                sx={{ ml: 1 }}
              >
                Search
              </Button>
            </Box>
            
            {/* Search results dropdown */}
            {showSearchResults && (
              <Paper
                elevation={3}
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 0.5,
                  maxHeight: 300,
                  overflow: 'auto',
                  zIndex: 1300,
                }}
              >
                {searchLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : searchResults.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="textSecondary">
                      No products found
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {searchResults.map((product) => (
                      <ListItem
                        key={product.id}
                        button
                        onClick={() => handleAddProduct(product)}
                      >
                        <ListItemText
                          primary={product.name}
                          secondary={`SKU: ${product.sku || 'N/A'} | Barcode: ${product.barcode || 'N/A'} | Price: ${formatCurrency(product.costPrice || product.purchasePrice || 0)}`}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddProduct(product);
                          }}
                        >
                          Add
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            )}
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="40">#</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Current Quantity</TableCell>
                  <TableCell>Current Purchase Price</TableCell>
                  <TableCell>Order Quantity</TableCell>
                  <TableCell>Order Purchase Price</TableCell>
                  <TableCell>Tax</TableCell>
                  <TableCell>Subtotal</TableCell>
                  <TableCell width="40"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" sx={{ py: 2 }}>
                        No products added. Search and add products above.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, index) => (
                    <TableRow key={item.product.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        {item.product.name}
                        <Typography variant="caption" display="block" color="textSecondary">
                          SKU: {item.product.sku || 'N/A'} | Barcode: {item.product.barcode || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.product.quantity || 0} Pc(s)</TableCell>
                      <TableCell>{formatCurrency(item.product.costPrice || item.product.purchasePrice || 0)}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={item.quantity}
                          onChange={(e) => handleUpdateQuantity(item.product.id, Number(e.target.value))}
                          inputProps={{ min: 1 }}
                          sx={{ width: '100px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={item.unitPrice}
                          onChange={(e) => handleUpdateUnitPrice(item.product.id, Number(e.target.value))}
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ width: '120px' }}
                        />
                      </TableCell>
                      <TableCell>
                        {item.product.tax ? `${item.product.tax.rate || 0}%` : '0%'}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(item.quantity * item.unitPrice)}
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          color="error" 
                          size="small"
                          onClick={() => handleRemoveItem(item.product.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Typography variant="body2">
              Total Items: {calculateTotals().totalItems}
            </Typography>
            <Typography variant="body2">
              Net Total Amount: {formatCurrency(calculateTotals().total)}
            </Typography>
            </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenPayment}
              disabled={items.length === 0}
            >
              Complete Purchase
            </Button>
        </Box>
        </StyledPaper>
      </Box>
      
      {/* Payment dialog */}
      <Dialog 
        open={paymentDialogOpen} 
        onClose={() => !processingOrder && setPaymentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Complete Purchase Order
          <IconButton
            aria-label="close"
            onClick={() => !processingOrder && setPaymentDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            disabled={processingOrder}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <POPayment
            totals={calculateTotals()}
            supplier={supplier}
            onCompletePurchase={handleCompletePurchase}
            loading={processingOrder}
            orderCompleted={Boolean(currentOrder)}
          />
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PurchaseOrder; 